import { ApiError, sendNotification, useApiClient, useTranslation } from '@msdk/common'
import { StringValue } from '@msdk/definitions'
import { CheckIcon, FormField, FormFieldLabel, TextInput } from '@msdk/ui'
import { ChangeEvent } from 'react'
import { useWizard } from 'react-use-wizard'

import { FooterPopup } from './FooterPopup'
import { HeaderPopup } from './HeaderPopup'
import {
  ModalStepProps,
  StyledContainer,
  StyledEditContent,
  StyledList,
  StyledListElement,
  StyledListItem,
  StyledMessageTypography,
} from './shared'

interface NicknamePopupProps extends ModalStepProps {
  nickname: StringValue | null
  onNicknameChange: (value: StringValue | null) => void
  isUnique: boolean | null
  onUniqueChange: (value: boolean | null) => void
}

export function NicknamePopup(props: NicknamePopupProps) {
  const { nextStep } = useWizard()
  const t = useTranslation()
  const apiClient = useApiClient()
  const value = props.nickname
  const { isUnique } = props
  const nickname = value ? value.toStringForView() : ''

  const requirements: { id: string; label: string; test: () => boolean }[] = [
    {
      id: 'length',
      label: t('LBL_NGID_USERNAME_RULE1', '5-15 characters'),
      test: () => nickname.length >= 5 && nickname.length <= 15,
    },
    {
      id: 'alphanumeric',
      label: t('LBL_NGID_USERNAME_RULE2', 'Letters and numbers only'),
      test: () => /^[a-zA-Z0-9]+$/.test(nickname),
    },
    {
      id: 'unique',
      label: t('LBL_NGID_USERNAME_RULE3', 'Unique within the clinic'),
      test: () => isUnique === true,
    },
  ]

  const disableContinue = !(requirements[0].test() && requirements[1].test())

  const onFieldChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value
    const fieldValue = newValue !== '' ? new StringValue(newValue) : null
    props.onNicknameChange(fieldValue)
    props.onUniqueChange(null)
  }

  const checkNicknameUnique = async (nicknameToCheck: string): Promise<boolean> => {
    const res = await apiClient.read<boolean>(`USM/api/NGID/IsNicknameUnique/${encodeURIComponent(nicknameToCheck)}`)
    if (res instanceof ApiError) {
      console.error('[UsernamePopup] - Error checking if username is unique', res)
      sendNotification({
        message: t('LBL_NGID_ERROR_CHECKING_USERNAME_UNIQUE', 'Error checking if username is unique'),
        status: 'error',
      })
      return false
    }
    return res
  }

  const handleStep = async () => {
    const unique = isUnique ? true : await checkNicknameUnique(nickname)
    props.onUniqueChange(unique)
    if (unique) {
      nextStep()
    }
  }

  return (
    <>
      <HeaderPopup title={t('LBL_NGID_USER_PASS_TITLE', 'Username and password')} modalRef={props.modalRef} />
      <StyledEditContent>
        <StyledMessageTypography variant="body1">
          {t(
            'LBL_NGID_USERNAME_DESCR',
            `A username is a short name used to login in to the dialysis machine. Pick something you'll remember.`,
          )}
        </StyledMessageTypography>
        <FormField required size="small">
          <FormFieldLabel>{t('LBL_NGID_USERNAME', 'Username')}</FormFieldLabel>
          <TextInput
            label={t('LBL_NGID_USERNAME', 'Username')}
            inputMode="text"
            size="small"
            onChange={onFieldChange}
            value={value ? value.toStringForView() : ''}
          />
        </FormField>
        <StyledContainer>
          <StyledListItem>{t('LBL_NGID_USERNAME_REQUIREMENTS', 'The username requirements:')}</StyledListItem>
          <StyledList>
            {requirements.map((r) => {
              const satisfied = r.test()
              const notSatisfied = r.id === 'unique' ? isUnique === false : nickname.length > 0 && !satisfied
              return (
                <StyledListElement key={r.id} $satisfied={satisfied} $notSatisfied={notSatisfied}>
                  {satisfied && (
                    <CheckIcon aria-hidden fontSize="small" style={{ position: 'absolute', left: '-1.25em' }} />
                  )}
                  {r.label}
                </StyledListElement>
              )
            })}
          </StyledList>
        </StyledContainer>
      </StyledEditContent>
      <FooterPopup
        primary={{ label: t('LBL_NGID_CONTINUE_BTN', 'Continue'), onClick: handleStep, disabled: disableContinue }}
      />
    </>
  )
}
